import React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Grid,
    Box,
    Typography,
    LinearProgress,
    Chip,
    Tooltip,
    Divider
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import useJobContext from "Pages/Job/JobView/useJobContext";
import { getStagesFromGroup } from "Pages/Pipeline/components/helperPipeline";
import { IMainState } from "Pages/Job/JobView/useJobContext/JobViewTypes";
import { getTotalFilteredApplicantsByStage } from "Pages/Job/JobView/JobKanban/helper";

interface JobFunnelQuickViewDialogProps {
    open: boolean;
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootContent: {
            padding: theme.spacing(3),
            background: theme.palette.background.default
        },
        groupCard: {
            borderRadius: 12,
            position: "relative",
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            padding: theme.spacing(2.5),
            minHeight: 280,
            display: "flex",
            flexDirection: "column",
            boxShadow:
                theme.palette.type === "light"
                    ? "0 2px 4px rgba(0,0,0,0.08)"
                    : "0 2px 6px rgba(0,0,0,0.5)"
        },
        groupHeader: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: theme.spacing(1)
        },
        groupBadge: {
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            padding: theme.spacing(0.5, 1),
            borderRadius: 8,
            color: theme.palette.common.white,
            display: "inline-flex",
            alignItems: "center"
        },
        badgeNew: { backgroundColor: "#2979ff" },
        badgeOthers: { backgroundColor: "#616161" },
        badgeHired: { backgroundColor: "#2e7d32" },
        badgeRejected: { backgroundColor: "#c62828" },
        stagesList: {
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(1),
            marginTop: theme.spacing(1),
            overflow: "auto"
        },
        chip: {
            borderRadius: 8,
            fontWeight: 500,
            maxWidth: 200
        },
        chipNew: {
            background: "#2979ff14",
            color: "#2979ff",
            border: "1px solid #2979ff55"
        },
        chipOthers: {
            background: "#61616114",
            color: "#616161",
            border: "1px solid #61616155"
        },
        chipHired: {
            background: "#2e7d3214",
            color: "#2e7d32",
            border: "1px solid #2e7d3255"
        },
        chipRejected: {
            background: "#c6282814",
            color: "#c62828",
            border: "1px solid #c6282855"
        },
        totalNumber: {
            fontSize: 28,
            fontWeight: 600,
            lineHeight: 1,
            marginRight: theme.spacing(1)
        },
        progressWrapper: {
            marginTop: theme.spacing(1)
        },
        progressBar: {
            height: 8,
            borderRadius: 4,
            background: "#e0e0e0"
        },
        divider: {
            margin: theme.spacing(1.5, 0)
        }
    })
);

export function JobFunnelQuickViewDialog({
    open,
    onClose
}: JobFunnelQuickViewDialogProps) {
    const [data] = useJobContext();
    const { t } = data;

    const groups = React.useMemo(() => {
        const stages = data.Job.data?.pipeline?.stages || [];
        const newGroup = getTotalStages(
            data,
            getStagesFromGroup("new", stages)
        );
        const othersGroup = getTotalStages(
            data,
            getStagesFromGroup("others", stages)
        );
        const hiredGroup = getTotalStages(
            data,
            getStagesFromGroup("hired", stages)
        );
        const rejectedGroup = getTotalStages(
            data,
            getStagesFromGroup("rejected", stages)
        );
        return [
            {
                key: "new",
                label: t("PIPELINEGROUP_new", "Novos"),
                color: "#2979ff",
                data: newGroup
            },
            {
                key: "others",
                label: t("PIPELINEGROUP_InProgress", "Em andamento"),
                color: "#616161",
                data: othersGroup
            },
            {
                key: "hired",
                label: t("PIPELINEGROUP_hired", "Contratado"),
                color: "#2e7d32",
                data: hiredGroup
            },
            {
                key: "rejected",
                label: t("PIPELINEGROUP_rejected", "Não Contratado"),
                color: "#c62828",
                data: rejectedGroup
            }
        ];
    }, [data, t]);

    const totalAll = groups.reduce((acc, g) => acc + g.data.groupTotal, 0) || 0;
    const classes = useStyles();

    interface StageWithTotal {
        id: string;
        name: string;
        totalStage: number;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="job-funnel-quick-view"
            maxWidth="lg"
            fullWidth
            scroll="paper"
        >
            <DialogContent className={classes.rootContent}>
                <Box
                    mb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        {t("JOBFUNNELQUICKVIEW_TITLE", "Resumo do Pipeline")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t("JOBFUNNELQUICKVIEW_TOTAL_APPLICANTS", {
                            defaultValue: "Total de candidatos: {{total}}",
                            total: totalAll
                        })}
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    {groups.map((group) => {
                        const pct = totalAll
                            ? (group.data.groupTotal / totalAll) * 100
                            : 0;
                        return (
                            <Grid item xs={12} md={6} lg={3} key={group.key}>
                                <Box className={classes.groupCard}>
                                    <div className={classes.groupHeader}>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 600 }}
                                        >
                                            {group.label}
                                        </Typography>
                                        <span
                                            className={`${classes.groupBadge} ${
                                                group.key === "new"
                                                    ? classes.badgeNew
                                                    : group.key === "others"
                                                    ? classes.badgeOthers
                                                    : group.key === "hired"
                                                    ? classes.badgeHired
                                                    : classes.badgeRejected
                                            }`}
                                        >
                                            {group.data.groupTotal}
                                        </span>
                                    </div>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {pct.toFixed(1)}%
                                    </Typography>
                                    <div className={classes.progressWrapper}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            className={classes.progressBar}
                                        />
                                    </div>
                                    <Divider className={classes.divider} />
                                    <Box
                                        flex={1}
                                        display="flex"
                                        flexDirection="column"
                                    >
                                        <div className={classes.stagesList}>
                                            {((group.data
                                                .stages as unknown) as StageWithTotal[]).map(
                                                (stage) => {
                                                    const stagePct = group.data
                                                        .groupTotal
                                                        ? Math.round(
                                                              (stage.totalStage /
                                                                  group.data
                                                                      .groupTotal) *
                                                                  100
                                                          )
                                                        : 0;
                                                    const label =
                                                        t(stage.name) ||
                                                        stage.name;
                                                    return (
                                                        <Tooltip
                                                            key={stage.id}
                                                            title={t(
                                                                "JOBFUNNELQUICKVIEW_STAGE_TOOLTIP",
                                                                {
                                                                    defaultValue:
                                                                        "{{stage}}: {{count}} candidatos ({{pct}}%)",
                                                                    stage: label,
                                                                    count:
                                                                        stage.totalStage,
                                                                    pct: stagePct
                                                                }
                                                            )}
                                                        >
                                                            <Chip
                                                                label={`(${stage.totalStage}) ${label}`}
                                                                size="small"
                                                                className={`${
                                                                    classes.chip
                                                                } ${
                                                                    group.key ===
                                                                    "new"
                                                                        ? classes.chipNew
                                                                        : group.key ===
                                                                          "others"
                                                                        ? classes.chipOthers
                                                                        : group.key ===
                                                                          "hired"
                                                                        ? classes.chipHired
                                                                        : classes.chipRejected
                                                                }`}
                                                            />
                                                        </Tooltip>
                                                    );
                                                }
                                            )}
                                            {!group.data.stages.length && (
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {t(
                                                        "JOBFUNNELQUICKVIEW_NO_STAGES",
                                                        "Sem etapas"
                                                    )}
                                                </Typography>
                                            )}
                                        </div>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    {t("COMPLEOGENERAL_CLOSE")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function getTotalStages(data: IMainState, stages: Compleo.IObject[]) {
    const listStageGroupIds = stages.map((item) => {
        const totalStage = getTotalFilteredApplicantsByStage(data, [item.id]);
        return {
            ...item,
            totalStage
        };
    });
    const groupTotal = listStageGroupIds.reduce(
        (acc, item) => acc + item.totalStage,
        0
    );
    return {
        stages: listStageGroupIds,
        groupTotal
    };
}
